import React, { useState, useEffect, useCallback } from 'react';
import { 

  Bot, Clock, CheckCircle, XCircle, AlertTriangle, Play, Pause, 
  Undo2, Eye, ChevronDown, ChevronRight, Filter, Search, RefreshCw,
  Shield, Zap, Lock, FileText, Users, BarChart3, Bell, Settings,
  Calendar, Hash, ArrowRight, X, Check, MessageSquare
} from 'lucide-react';
import { apiFetch } from '../services/api_service';

const API = process.env.REACT_APP_BACKEND_URL;

// Tier badge component
const TierBadge = ({ tier }) => {
  const config = {
    tier_1: { icon: Zap, color: 'bg-green-100 text-green-700', label: 'Auto' },
    tier_2: { icon: Clock, color: 'bg-amber-100 text-amber-700', label: 'Delayed' },
    tier_3: { icon: Lock, color: 'bg-red-100 text-red-700', label: 'Approval' }
  };
  
  const { icon: Icon, color, label } = config[tier] || config.tier_3;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      <Icon size={12} />
      {label}
    </span>
  );
};

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    pending: { color: 'bg-slate-100 text-slate-600', label: 'Pending' },
    scheduled: { color: 'bg-blue-100 text-blue-700', label: 'Scheduled' },
    awaiting_approval: { color: 'bg-purple-100 text-purple-700', label: 'Awaiting Approval' },
    approved: { color: 'bg-emerald-100 text-emerald-700', label: 'Approved' },
    executing: { color: 'bg-amber-100 text-amber-700', label: 'Executing' },
    executed: { color: 'bg-green-100 text-green-700', label: 'Executed' },
    rejected: { color: 'bg-red-100 text-red-700', label: 'Rejected' },
    failed: { color: 'bg-red-100 text-red-700', label: 'Failed' },
    undone: { color: 'bg-slate-100 text-slate-600', label: 'Undone' },
    cancelled: { color: 'bg-slate-100 text-slate-600', label: 'Cancelled' }
  };
  
  const { color, label } = config[status] || { color: 'bg-slate-100 text-slate-600', label: status };
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {label}
    </span>
  );
};

// Time remaining component
const TimeRemaining = ({ scheduledExecution }) => {
  const [remaining, setRemaining] = useState('');
  
  useEffect(() => {
    if (!scheduledExecution) return;
    
    const updateRemaining = () => {
      const now = new Date();
      const target = new Date(scheduledExecution);
      const diff = target - now;
      
      if (diff <= 0) {
        setRemaining('Executing...');
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 0) {
        setRemaining(`${hours}h ${minutes}m`);
      } else {
        setRemaining(`${minutes}m`);
      }
    };
    
    updateRemaining();
    const interval = setInterval(updateRemaining, 60000);
    return () => clearInterval(interval);
  }, [scheduledExecution]);
  
  if (!scheduledExecution) return null;
  
  return (
    <span className="text-sm text-amber-600 font-medium">
      Auto-executes in: {remaining}
    </span>
  );
};

// Action Card Component
const ActionCard = ({ action, onApprove, onReject, onCancel, onUndo, onViewDetails, expanded, onToggleExpand }) => {
  const [approvalPassword, setApprovalPassword] = useState('');
  const [approvalComment, setApprovalComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  
  const canApprove = action.status === 'awaiting_approval';
  const canReject = ['awaiting_approval', 'scheduled', 'pending'].includes(action.status);
  const canCancel = ['scheduled', 'pending', 'awaiting_approval'].includes(action.status);
  const canUndo = action.status === 'executed' && action.reversible;
  
  const handleApprove = () => {
    if (approvalPassword) {
      onApprove(action.action_id, approvalPassword, approvalComment);
      setShowApprovalForm(false);
      setApprovalPassword('');
      setApprovalComment('');
    }
  };
  
  const handleReject = () => {
    if (rejectReason) {
      onReject(action.action_id, rejectReason);
      setShowRejectForm(false);
      setRejectReason('');
    }
  };
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-slate-50"
        onClick={() => onToggleExpand(action.action_id)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${
              action.tier === 'tier_3' ? 'bg-red-100' :
              action.tier === 'tier_2' ? 'bg-amber-100' : 'bg-green-100'
            }`}>
              <Bot size={20} className={
                action.tier === 'tier_3' ? 'text-red-600' :
                action.tier === 'tier_2' ? 'text-amber-600' : 'text-green-600'
              } />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-slate-900">
                  {action.action?.type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </h3>
                <TierBadge tier={action.tier} />
                <StatusBadge status={action.status} />
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Agent: {action.triggered_by?.agent_id || 'Unknown'} • 
                Target: {action.action?.target_module}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {action.status === 'scheduled' && (
              <TimeRemaining scheduledExecution={action.scheduled_execution} />
            )}
            {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </div>
        </div>
      </div>
      
      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100">
          {/* Reasoning */}
          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            <p className="text-sm font-medium text-slate-700 mb-1">AI Reasoning:</p>
            <p className="text-sm text-slate-600">{action.action?.reasoning}</p>
          </div>
          
          {/* Affected Records */}
          {action.action?.affected_records?.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium text-slate-700 mb-1">
                Affects {action.action.affected_records.length} record(s):
              </p>
              <div className="flex flex-wrap gap-1">
                {action.action.affected_records.slice(0, 5).map((record, i) => (
                  <span key={i} className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
                    {record}
                  </span>
                ))}
                {action.action.affected_records.length > 5 && (
                  <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
                    +{action.action.affected_records.length - 5} more
                  </span>
                )}
              </div>
            </div>
          )}
          
          {/* Approval Status for Tier 3 */}
          {action.tier === 'tier_3' && action.approval && (
            <div className="mt-3 p-3 bg-purple-50 rounded-lg">
              <p className="text-sm font-medium text-purple-700 mb-2">Approval Status:</p>
              <div className="space-y-1">
                {action.approval.required_approvers?.map((approver, i) => {
                  const approved = action.approval.approvals?.find(a => `role:${a.role}` === approver);
                  return (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      {approved ? (
                        <CheckCircle size={14} className="text-green-600" />
                      ) : (
                        <Clock size={14} className="text-slate-400" />
                      )}
                      <span className={approved ? 'text-green-700' : 'text-slate-600'}>
                        {approver.replace('role:', '').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                      {approved && (
                        <span className="text-xs text-slate-500">
                          - {approved.user_email}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => onViewDetails(action.action_id)}
              className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center gap-1"
            >
              <Eye size={14} />
              View Details
            </button>
            
            {canApprove && !showApprovalForm && (
              <button
                onClick={() => setShowApprovalForm(true)}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-1"
              >
                <Check size={14} />
                Approve
              </button>
            )}
            
            {canReject && !showRejectForm && (
              <button
                onClick={() => setShowRejectForm(true)}
                className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 flex items-center gap-1"
              >
                <X size={14} />
                Reject
              </button>
            )}
            
            {canCancel && (
              <button
                onClick={() => onCancel(action.action_id)}
                className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center gap-1"
              >
                <Pause size={14} />
                Cancel
              </button>
            )}
            
            {canUndo && (
              <button
                onClick={() => onUndo(action.action_id)}
                className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-200 flex items-center gap-1"
              >
                <Undo2 size={14} />
                Undo
              </button>
            )}
            
            {action.status === 'scheduled' && (
              <button
                onClick={() => onApprove(action.action_id, '', 'Execute now')}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1"
              >
                <Play size={14} />
                Execute Now
              </button>
            )}
          </div>
          
          {/* Approval Form */}
          {showApprovalForm && (
            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-medium text-green-800 mb-3">Approve Action</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-green-700 mb-1">
                    Password (for digital signature)
                  </label>
                  <input
                    type="password"
                    value={approvalPassword}
                    onChange={(e) => setApprovalPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Enter your password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-green-700 mb-1">
                    Comment (optional)
                  </label>
                  <input
                    type="text"
                    value={approvalComment}
                    onChange={(e) => setApprovalComment(e.target.value)}
                    className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Add a comment"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleApprove}
                    disabled={!approvalPassword}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
                  >
                    Confirm Approval
                  </button>
                  <button
                    onClick={() => setShowApprovalForm(false)}
                    className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Reject Form */}
          {showRejectForm && (
            <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
              <h4 className="font-medium text-red-800 mb-3">Reject Action</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-red-700 mb-1">
                    Reason for rejection
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="Explain why this action should not be executed"
                    rows={2}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleReject}
                    disabled={!rejectReason}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
                  >
                    Confirm Rejection
                  </button>
                  <button
                    onClick={() => setShowRejectForm(false)}
                    className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Component
const ActionQueue = () => {
  const [actions, setActions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedAction, setExpandedAction] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const fetchActions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      let url = `${API}/api/agent-actions/queue?limit=50`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (tierFilter) url += `&tier=${tierFilter}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActions(data.actions || []);
        setStats(data.stats || {});
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, tierFilter]);
  
  useEffect(() => {
    fetchActions();
    // Refresh every 30 seconds
    const interval = setInterval(fetchActions, 30000);
    return () => clearInterval(interval);
  }, [fetchActions]);
  
  const handleApprove = async (actionId, password, comment) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/agent-actions/${actionId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password, comment })
      });
      
      if (response.ok) {
        fetchActions();
      } else {
        const error = await response.json().catch(() => ({}));
        alert(error.detail || 'Approval failed');
      }
    } catch (err) {
      alert('Error approving action');
    }
  };
  
  const handleReject = async (actionId, reason) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/agent-actions/${actionId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });
      
      if (response.ok) {
        fetchActions();
      } else {
        const error = await response.json().catch(() => ({}));
        alert(error.detail || 'Rejection failed');
      }
    } catch (err) {
      alert('Error rejecting action');
    }
  };
  
  const handleCancel = async (actionId) => {
    if (!confirm('Are you sure you want to cancel this action?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/agent-actions/${actionId}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchActions();
      } else {
        const error = await response.json().catch(() => ({}));
        alert(error.detail || 'Cancellation failed');
      }
    } catch (err) {
      alert('Error cancelling action');
    }
  };
  
  const handleUndo = async (actionId) => {
    if (!confirm('Are you sure you want to undo this action?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/agent-actions/${actionId}/undo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchActions();
      } else {
        const error = await response.json().catch(() => ({}));
        alert(error.detail || 'Undo failed');
      }
    } catch (err) {
      alert('Error undoing action');
    }
  };
  
  const handleViewDetails = (actionId) => {
    // Could open a modal or navigate to detail page
    setExpandedAction(expandedAction === actionId ? null : actionId);
  };
  
  const filteredActions = actions.filter(action => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        action.action?.type?.toLowerCase().includes(query) ||
        action.action?.reasoning?.toLowerCase().includes(query) ||
        action.triggered_by?.agent_id?.toLowerCase().includes(query)
      );
    }
    return true;
  });
  
  // Group actions by priority
  const tier3Actions = filteredActions.filter(a => a.tier === 'tier_3' && a.status === 'awaiting_approval');
  const tier2Actions = filteredActions.filter(a => a.tier === 'tier_2' && a.status === 'scheduled');
  const recentActions = filteredActions.filter(a => 
    a.status === 'executed' || a.status === 'rejected' || a.status === 'undone'
  ).slice(0, 10);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="animate-spin text-emerald-600" size={32} />
        <span className="ml-3 text-slate-600">Loading action queue...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="action-queue-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Bot size={32} />
              <h1 className="text-3xl font-bold">AI Review & Approvals</h1>
            </div>
            <p className="text-indigo-200">
              Human-in-the-Loop Automation • Review, approve, or modify AI agent actions
            </p>
          </div>
          <button
            onClick={fetchActions}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg flex items-center gap-2"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-purple-600 mb-2">
            <Clock size={18} />
            <span className="text-sm font-medium">Pending</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats.pending || 0}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-amber-600 mb-2">
            <Calendar size={18} />
            <span className="text-sm font-medium">Today</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats.executed_today || 0}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-green-600 mb-2">
            <CheckCircle size={18} />
            <span className="text-sm font-medium">Approved</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats.approved_today || 0}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-red-600 mb-2">
            <XCircle size={18} />
            <span className="text-sm font-medium">Rejected</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats.rejected_today || 0}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-blue-600 mb-2">
            <BarChart3 size={18} />
            <span className="text-sm font-medium">Total</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{actions.length}</p>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-xl border p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search actions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Statuses</option>
            <option value="awaiting_approval">Awaiting Approval</option>
            <option value="scheduled">Scheduled</option>
            <option value="executed">Executed</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Tiers</option>
            <option value="tier_1">Tier 1 (Auto)</option>
            <option value="tier_2">Tier 2 (Delayed)</option>
            <option value="tier_3">Tier 3 (Approval)</option>
          </select>
        </div>
      </div>
      
      {/* High Priority - Tier 3 */}
      {tier3Actions.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Lock size={20} className="text-red-600" />
            <h2 className="text-lg font-bold text-slate-900">Requires Your Approval</h2>
            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-sm font-medium">
              {tier3Actions.length}
            </span>
          </div>
          <div className="space-y-3">
            {tier3Actions.map(action => (
              <ActionCard
                key={action.action_id}
                action={action}
                expanded={expandedAction === action.action_id}
                onToggleExpand={handleViewDetails}
                onApprove={handleApprove}
                onReject={handleReject}
                onCancel={handleCancel}
                onUndo={handleUndo}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Medium Priority - Tier 2 */}
      {tier2Actions.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Clock size={20} className="text-amber-600" />
            <h2 className="text-lg font-bold text-slate-900">Scheduled Actions</h2>
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
              {tier2Actions.length}
            </span>
          </div>
          <div className="space-y-3">
            {tier2Actions.map(action => (
              <ActionCard
                key={action.action_id}
                action={action}
                expanded={expandedAction === action.action_id}
                onToggleExpand={handleViewDetails}
                onApprove={handleApprove}
                onReject={handleReject}
                onCancel={handleCancel}
                onUndo={handleUndo}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Recent Actions */}
      {recentActions.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle size={20} className="text-green-600" />
            <h2 className="text-lg font-bold text-slate-900">Recently Completed</h2>
          </div>
          <div className="space-y-3">
            {recentActions.map(action => (
              <ActionCard
                key={action.action_id}
                action={action}
                expanded={expandedAction === action.action_id}
                onToggleExpand={handleViewDetails}
                onApprove={handleApprove}
                onReject={handleReject}
                onCancel={handleCancel}
                onUndo={handleUndo}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Empty State */}
      {filteredActions.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border">
          <Bot size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-600">No actions in queue</h3>
          <p className="text-slate-400 mt-1">
            AI agents will propose actions here for your review
          </p>
        </div>
      )}
      
      {/* Info Banner */}
      <div className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-xl p-6 text-white">
        <div className="flex items-start gap-4">
          <Shield size={24} className="flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-bold text-lg mb-1">Human-in-the-Loop Compliance</h3>
            <p className="text-slate-300 text-sm">
              All high-risk actions require explicit human approval. Medium-risk actions auto-execute 
              after a delay unless cancelled. All actions are logged in an immutable audit trail 
              for regulatory compliance (CSRD, EU AI Act, SOX).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionQueue;
